import clsx from "clsx";
import { CSSProperties, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { SelectOptionsDropdown } from "@ryde/controls";
import {
  DateYMDString,
  IRidesCalendarEventDto,
  selectedTableRowsIdsAtomFamily,
  SelectionEnum,
  timezoneAdjustedDateToDayStartISO,
  timezoneAdjustedDateToISO,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import {
  applySelectionToIds,
  computeSelectionState,
  getRideIdsForColumn,
} from "../rides-calendar.helpers";
import {
  RIDES_CALENDAR_DATE_PART_PAD_LENGTH,
  RIDES_CALENDAR_ENTITY,
} from "./rides-calendar.constants";

interface IHeadDateProps {
  style: CSSProperties;
  columnIndex: number;
  dateKeys: DateYMDString[];
  eventMap: Map<string, IRidesCalendarEventDto[]>;
}

const todayKey = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(
    RIDES_CALENDAR_DATE_PART_PAD_LENGTH,
    "0",
  );
  const dd = String(d.getDate()).padStart(
    RIDES_CALENDAR_DATE_PART_PAD_LENGTH,
    "0",
  );
  return `${yyyy}-${mm}-${dd}`;
};

export const RidesCalendarHeadDateCell: FC<IHeadDateProps> = ({
  style,
  columnIndex,
  dateKeys,
  eventMap,
}) => {
  const { i18n } = useTranslation();
  const { formatDayOfWeekTranslated, formatDateNormalized } = useFormats();
  const { timezone } = useTimezone();
  const ymd = dateKeys[columnIndex];
  const dateYmd = String(ymd) as DateYMDString;
  const isToday = dateYmd === todayKey();

  const [selectedIds, setSelectedIds] = useRecoilState(
    selectedTableRowsIdsAtomFamily(RIDES_CALENDAR_ENTITY),
  );

  const columnIds = useMemo(
    () => getRideIdsForColumn(eventMap, dateYmd),
    [eventMap, dateYmd],
  );
  const selectionState = useMemo(
    () => computeSelectionState(columnIds, new Set(selectedIds)),
    [columnIds, selectedIds],
  );

  const handleSetChecked = (value: SelectionEnum): void => {
    setSelectedIds(prev =>
      applySelectionToIds({ ids: columnIds, selectedIds: prev, value }),
    );
  };

  const { dow, dmy } = useMemo(() => {
    if (!ymd) {
      return { dow: "", dmy: "" };
    }
    const startIso = timezoneAdjustedDateToDayStartISO(ymd, timezone);
    const fullIso = timezoneAdjustedDateToISO(ymd, timezone);
    return {
      dow: formatDayOfWeekTranslated(
        i18n.language,
        { weekday: "short" },
        "upper",
        startIso,
      ),
      dmy: formatDateNormalized(fullIso),
    };
  }, [
    formatDateNormalized,
    formatDayOfWeekTranslated,
    i18n.language,
    timezone,
    ymd,
  ]);

  return (
    <div
      className={clsx("rc-head-cell", isToday && "rc-head-cell--today")}
      style={style}
    >
      <span className="rc-head-cell__select">
        <SelectOptionsDropdown
          checked={selectionState}
          setChecked={handleSetChecked}
        />
      </span>
      <span className="rc-head-cell__dow">{dow}</span>
      <span className="rc-head-cell__dmy">{dmy}</span>
    </div>
  );
};
